import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  signInWithGoogle,
  signUpWithEmail,
  signInWithEmail,
  resetPassword,
  logOut,
  onAuthChange,
  getIdToken,
} from '@/services/firebase';
import type { User } from 'firebase/auth';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const loading = ref(true);
  const error = ref('');

  const isAuthenticated = computed(() => !!user.value);
  const displayName = computed(() => user.value?.displayName || '');
  const email = computed(() => user.value?.email || '');
  const photoURL = computed(() => user.value?.photoURL || '');

  function init() {
    onAuthChange(async (firebaseUser) => {
      const wasLoggedOut = !user.value;
      user.value = firebaseUser;
      loading.value = false;

      // On login, sync data and check subscription
      if (firebaseUser && wasLoggedOut) {
        try {
          const { useProfileStore } = await import('@/stores/profile.store');
          const profileStore = useProfileStore();
          await profileStore.syncToServer();
          await profileStore.loadFromServer();

          // Sync people data
          const { usePeopleStore } = await import('@/stores/people.store');
          const peopleStore = usePeopleStore();
          await peopleStore.syncToServer();
          await peopleStore.loadFromServer();

          const { useSubscriptionStore } = await import('@/stores/subscription.store');
          const subStore = useSubscriptionStore();
          await subStore.checkStatus();

          // Re-register push token if permission was already granted
          // (new permission requests are triggered by user gesture on the home page)
          try {
            if ('Notification' in window && Notification.permission === 'granted') {
              const { requestPushPermission } = await import('@/services/push-notifications');
              await requestPushPermission();
            }
          } catch (pushErr) {
            console.warn('Push notification registration failed (non-fatal)', pushErr);
          }

          // Check for pending couple invites addressed to this user
          const { checkPendingInvitesForMe } = await import('@/services/couple.service');
          const { invites } = await checkPendingInvitesForMe();
          if (invites.length > 0) {
            // Store pending invite for the home page to pick up
            localStorage.setItem('inflection-pending-invite', JSON.stringify(invites[0]));
          }
        } catch (err) {
          console.warn('Data sync on login failed', err);
        }
      }
    });
  }

  async function loginWithGoogle() {
    error.value = '';
    try {
      await signInWithGoogle();
    } catch (err: any) {
      error.value = err.message || 'Sign in failed';
    }
  }

  async function loginWithEmail(emailAddr: string, password: string) {
    error.value = '';
    try {
      await signInWithEmail(emailAddr, password);
    } catch (err: any) {
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        error.value = 'Incorrect email or password';
      } else if (err.code === 'auth/user-not-found') {
        error.value = 'No account found with this email';
      } else if (err.code === 'auth/too-many-requests') {
        error.value = 'Too many attempts. Please try again later';
      } else {
        error.value = err.message || 'Sign in failed';
      }
    }
  }

  async function register(emailAddr: string, password: string, name: string) {
    error.value = '';
    try {
      await signUpWithEmail(emailAddr, password, name);
    } catch (err: any) {
      if (err.code === 'auth/email-already-in-use') {
        error.value = 'An account with this email already exists';
      } else if (err.code === 'auth/weak-password') {
        error.value = 'Password must be at least 6 characters';
      } else if (err.code === 'auth/invalid-email') {
        error.value = 'Please enter a valid email address';
      } else {
        error.value = err.message || 'Registration failed';
      }
    }
  }

  async function forgotPassword(emailAddr: string) {
    error.value = '';
    try {
      await resetPassword(emailAddr);
      return true;
    } catch (err: any) {
      if (err.code === 'auth/user-not-found') {
        error.value = 'No account found with this email';
      } else {
        error.value = err.message || 'Failed to send reset email';
      }
      return false;
    }
  }

  // Keep backwards-compatible `login` as alias for Google
  async function login() {
    return loginWithGoogle();
  }

  async function logout() {
    await logOut();
    user.value = null;
    localStorage.removeItem('16p-profile');
    localStorage.removeItem('16p-analyses');
    localStorage.removeItem('16p-people');
    localStorage.removeItem('16p-groups');
  }

  async function getToken(): Promise<string | null> {
    return getIdToken();
  }

  return {
    user,
    loading,
    error,
    isAuthenticated,
    displayName,
    email,
    photoURL,
    init,
    login,
    loginWithGoogle,
    loginWithEmail,
    register,
    forgotPassword,
    logout,
    getToken,
  };
});
